<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <!-- Locale switcher -->
    <div class="absolute top-4 right-4 z-10">
      <div class="locale-switcher relative" v-click-outside="closeDropdown">
        <button
          @click="toggleDropdown"
          class="current-locale bg-white rounded-lg shadow-sm border border-gray-200 px-3 py-2 cursor-pointer hover:border-gray-300 transition-colors"
        >
          <div class="flex items-center gap-2">
            <img
              :src="currentLocaleData.flag"
              :alt="currentLocale"
              class="w-4 h-3 object-cover rounded-sm"
            />
            <span class="text-xs font-medium text-gray-700 uppercase">{{ currentLocale }}</span>
            <svg
              :class="[
                'w-3 h-3 text-gray-400 transition-transform',
                isDropdownOpen ? 'rotate-180' : '',
              ]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          </div>
        </button>
        <div
          v-show="isDropdownOpen"
          class="dropdown-menu absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[120px] animate-in fade-in duration-200"
        >
          <button
            v-for="loc in availableLocales"
            :key="loc.code"
            @click="switchLocale(loc.code)"
            :class="[
              'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors',
              currentLocale === loc.code
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
            ]"
            :title="loc.name"
          >
            <img :src="loc.flag" :alt="loc.code" class="w-4 h-3 object-cover rounded-sm" />
            <span>{{ loc.name }}</span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="hasConsumptionWarning && !loading && !genericLanding"
      role="status"
      class="max-w-2xl mx-auto px-4 mb-4"
    >
      <div class="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        {{ t('consumptionWarning') }}
      </div>
    </div>

    <!-- Generic landing when no namespace present in URL -->
    <div v-if="genericLanding" class="max-w-2xl mx-auto px-6 py-20 text-center space-y-6">
      <div class="flex flex-col items-center gap-4">
        <img src="/images/campingonline_logo.svg" alt="CampingOnline" class="h-12" />
        <h1 class="text-2xl font-semibold text-gray-900">
          {{ t('landingTitle') }}
        </h1>
        <p class="text-gray-600 leading-relaxed text-sm sm:text-base max-w-prose">
          {{ t('landingDescription') }}
        </p>
        <p class="text-gray-500 text-sm">
          {{ t('landingLinkRequired') }}
        </p>
        <div>
          <a
            href="https://www.campingonline.se"
            class="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white text-sm font-medium shadow-sm hover:opacity-90 transition"
          >
            {{ t('landingVisitSite') }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </a>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-else-if="loading" class="max-w-2xl mx-auto px-4">
      <div class="flex flex-col items-center justify-center py-24 gap-6" aria-live="polite">
        <div
          class="relative w-14 h-14 flex items-center justify-center"
          role="status"
          :aria-label="t('loading')"
        >
          <svg class="animate-spin text-brand" viewBox="0 0 50 50">
            <circle
              class="opacity-10 stroke-current"
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke-width="4"
            />
            <path
              class="stroke-current"
              fill="none"
              stroke-width="4"
              stroke-linecap="round"
              d="M25 5a20 20 0 1 1-14.14 34.14"
            />
          </svg>
        </div>
        <div class="flex flex-col items-center gap-1">
          <p class="text-sm text-gray-600 font-medium">
            {{ t('fetchingBooking') }}
          </p>
          <p class="text-xs text-gray-400 tracking-wide">
            {{ t('pleaseWait') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="displayLoadError" class="max-w-2xl mx-auto px-4" aria-live="polite">
      <div class="text-center mb-8 mt-4">
        <img
          v-if="branding.logo"
          :src="branding.logo"
          :alt="branding.name"
          class="max-h-30 min-h-12 max-w-xs mx-auto mb-4 object-contain"
        />
      </div>
      <div :class="['border rounded-lg p-5 space-y-4', errorStyling.containerClass]">
        <div class="flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v4m0 4h.01M4.93 4.93l14.14 14.14M12 2a10 10 0 100 20 10 10 0 000-20z"
            />
          </svg>
          <p class="font-medium">{{ errorStyling.title }}</p>
        </div>
        <p class="text-sm leading-relaxed">{{ displayLoadError }}</p>
      </div>
    </div>

    <!-- Checkout completed (no payment needed or payment confirmed) -->
    <div v-else-if="checkoutCompleted" class="max-w-2xl mx-auto px-4">
      <div class="text-center mb-8 mt-4">
        <img
          v-if="branding.logo"
          :src="branding.logo"
          :alt="branding.name"
          class="max-h-30 min-h-12 max-w-xs mx-auto mb-4 object-contain"
        />
      </div>
      <div class="card p-6 mb-6">
        <div class="text-center space-y-4">
          <div class="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              class="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 class="text-xl font-semibold text-gray-900">
            {{ t('checkoutConfirmedTitle') }}
          </h2>
          <p class="text-gray-600">{{ t('checkoutConfirmedSubtitle') }}</p>
          <div
            v-if="paymentConfirmed && remainingAmount === null"
            class="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700"
          >
            <p class="font-medium">{{ t('paymentReceivedTitle') }}</p>
            <p>
              {{ t('paymentReceivedSubtitle')
              }}<template v-if="confirmationEmail">
                {{ ' ' }}<span class="font-medium">{{ confirmationEmail }}</span></template
              ><template v-else>.</template>
            </p>
          </div>
        </div>

        <div
          v-if="remainingAmount !== null"
          class="mt-6 rounded-lg border p-4"
          :class="
            remainingAmount > 0 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'
          "
        >
          <p class="font-medium">
            {{ remainingAmount > 0 ? t('paymentRemaining') : t('nothingToPay') }}
          </p>
          <p v-if="remainingAmount > 0" class="mt-2 text-sm">
            {{ t('checkedOutPaymentRemaining') }}
          </p>
          <div
            v-for="invoice in outstandingInvoices"
            :key="invoice.id"
            class="mt-4 flex flex-wrap items-center justify-between gap-3"
          >
            <span class="text-sm"
              >{{ t('invoice') }} {{ invoice.invoice_number }} ·
              {{ formatCurrency(oreToSEK(invoice.remaining_amount)) }}</span
            >
            <a v-if="invoice.payment_link" :href="invoice.payment_link" class="btn-primary">{{
              t('payInvoice')
            }}</a>
            <span v-else class="text-sm">{{ t('invoicePaymentInstructions') }}</span>
          </div>
        </div>

        <!-- Booking summary in success state -->
        <div class="mt-6 bg-gray-50 rounded-lg p-4">
          <h3 class="text-sm font-medium text-gray-900 mb-3">
            {{ t('bookingSummary') }}
          </h3>
          <dl class="text-sm text-gray-700 space-y-1.5">
            <div class="flex justify-between">
              <dt class="text-gray-500">{{ t('bookingNumber') }}</dt>
              <dd class="text-gray-900">{{ booking.bookingNumber }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-gray-500">
                {{ booking.positionLabel || t('pitch') }}
              </dt>
              <dd class="text-gray-900">{{ booking.positionName }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-gray-500">{{ t('checkIn') }}</dt>
              <dd class="text-gray-900">{{ formatDate(booking.checkIn) }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-gray-500">{{ t('checkOut') }}</dt>
              <dd class="text-gray-900">{{ formatDate(booking.checkOut) }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Kontaktinformation -->
      <div v-if="branding.phone || branding.email" class="card p-4 mb-6">
        <p class="text-sm text-gray-600">
          {{ t('contactQuestions') }}
          <a
            v-if="branding.email"
            :href="`mailto:${branding.email}`"
            class="text-brand hover:underline"
            >{{ branding.email }}</a
          >
          <span v-if="branding.phone && branding.email" class="mx-1">·</span>
          <a v-if="branding.phone" :href="phoneHref" class="text-brand hover:underline">{{
            branding.phone
          }}</a>
        </p>
      </div>

      <!-- Feedback -->
      <div v-if="booking.bookingNumber" class="card p-6 mb-6">
        <div v-if="feedbackSubmitted" class="text-center py-4">
          <p class="text-green-600 font-medium">{{ t('feedbackThanks') }}</p>
        </div>
        <div v-else>
          <h3 class="text-sm font-medium text-gray-900 mb-4">
            {{ t('feedbackTitle') }}
          </h3>
          <div class="flex justify-center gap-2 mb-4">
            <button
              v-for="star in 5"
              :key="star"
              @click="feedbackRating = star"
              @mouseenter="feedbackHoverRating = star"
              @mouseleave="feedbackHoverRating = 0"
              class="text-3xl transition-colors focus:outline-none cursor-pointer"
              :class="
                star <= (feedbackHoverRating || feedbackRating)
                  ? feedbackHoverRating
                    ? 'text-yellow-300'
                    : 'text-yellow-400'
                  : 'text-gray-300'
              "
              :aria-label="`${star} stars`"
            >
              ★
            </button>
          </div>
          <textarea
            v-model="feedbackComment"
            :placeholder="t('feedbackPlaceholder')"
            class="w-full border border-gray-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
            rows="3"
          ></textarea>
          <button
            @click="submitFeedback"
            :disabled="feedbackRating === 0 || feedbackSubmitting"
            class="mt-3 w-full bg-brand text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition"
          >
            {{ feedbackSubmitting ? t('pleaseWaitAction') : t('feedbackSubmit') }}
          </button>
        </div>
      </div>

      <!-- CampingOnline branding footer -->
      <div class="text-center">
        <div class="text-sm text-gray-500 mb-2">
          {{ t('selfCheckoutBranding') }}
        </div>
        <div class="flex items-center justify-center">
          <img src="/images/campingonline_logo.svg" alt="CampingOnline" class="h-8" />
        </div>
      </div>
    </div>

    <!-- Waiting for payment (redirecting or polling) -->
    <div v-else-if="waitingForPayment" class="max-w-2xl mx-auto px-4">
      <div class="text-center mb-8 mt-4">
        <img
          v-if="branding.logo"
          :src="branding.logo"
          :alt="branding.name"
          class="max-h-30 min-h-12 max-w-xs mx-auto mb-4 object-contain"
        />
      </div>
      <div class="card p-6 mb-6">
        <div class="flex flex-col items-center justify-center py-8 gap-6">
          <div class="relative w-14 h-14 flex items-center justify-center" role="status">
            <svg class="animate-spin text-brand" viewBox="0 0 50 50">
              <circle
                class="opacity-10 stroke-current"
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke-width="4"
              />
              <path
                class="stroke-current"
                fill="none"
                stroke-width="4"
                stroke-linecap="round"
                d="M25 5a20 20 0 1 1-14.14 34.14"
              />
            </svg>
          </div>
          <div class="flex flex-col items-center gap-1">
            <p class="text-sm text-gray-600 font-medium">
              {{ t('verifyingPayment') }}
            </p>
            <p class="text-xs text-gray-400">{{ t('pleaseWait') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content: Preview -->
    <div v-else class="max-w-2xl mx-auto px-4">
      <!-- Header with camping logo -->
      <div class="text-center mb-8">
        <img
          v-if="branding.logo"
          :src="branding.logo"
          :alt="branding.name"
          class="max-h-30 min-h-12 max-w-xs mx-auto mb-4 object-contain"
        />
        <div
          v-else-if="branding.name"
          class="h-16 mx-auto mb-4 flex items-center justify-center rounded-sm bg-gray-200 text-gray-700 font-semibold text-xl px-4"
        >
          {{ branding.name }}
        </div>
      </div>

      <!-- Negativ balans – be gästen komma till receptionen -->
      <div v-if="hasNegativeTotal" class="card p-6 mb-6">
        <div class="flex flex-col items-center text-center gap-4 py-4">
          <div class="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
            <svg
              class="w-7 h-7 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="space-y-2">
            <h3 class="text-lg font-semibold text-gray-900">
              {{ t('negativeBalanceTitle') }}
            </h3>
            <p class="text-sm text-gray-600 max-w-sm">
              {{ t('negativeBalanceBody') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Booking info card -->
      <div v-if="!hasNegativeTotal" class="card p-6 mb-6">
        <div class="flex flex-col gap-4 mb-6">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 class="text-xl font-semibold text-gray-900 leading-tight">
                {{ t('selfCheckout') }}
              </h2>
              <p class="text-sm text-gray-500 mt-1">
                {{
                  customerFirstName
                    ? t('welcomeGreeting', { name: customerFirstName })
                    : booking.customerName
                }}
              </p>
            </div>
          </div>

          <!-- Booking meta -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4 border-t border-gray-100">
            <div>
              <h3 class="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-3">
                {{ t('bookingDetails') }}
              </h3>
              <dl class="text-sm text-gray-700 space-y-1.5">
                <div class="flex justify-between">
                  <dt class="text-gray-500">{{ t('bookingNumber') }}</dt>
                  <dd class="text-gray-900">{{ booking.bookingNumber }}</dd>
                </div>
                <div class="flex justify-between">
                  <dt class="text-gray-500">
                    {{ booking.positionLabel || t('pitch') }}
                  </dt>
                  <dd class="text-gray-900">{{ booking.positionName }}</dd>
                </div>
                <div class="flex justify-between">
                  <dt class="text-gray-500">{{ t('checkIn') }}</dt>
                  <dd class="text-gray-900">
                    {{ formatDate(booking.checkIn) }}
                  </dd>
                </div>
                <div class="flex justify-between">
                  <dt class="text-gray-500">{{ t('checkOut') }}</dt>
                  <dd class="text-gray-900">
                    {{ formatDate(booking.checkOut) }}
                  </dd>
                </div>
              </dl>
            </div>
            <div>
              <h3 class="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-3">
                {{ t('checkOutDeadline') }}
              </h3>
              <div class="flex items-center gap-2">
                <svg
                  class="w-5 h-5 text-amber-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span class="text-lg font-semibold text-gray-900">{{
                  formatTime(booking.checkOutTime)
                }}</span>
              </div>
              <p class="text-xs text-gray-500 mt-1">
                {{ t('checkOutDeadlineHelp') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Accommodation items -->
        <div v-if="accommodationItems.length" class="mb-4 bg-gray-50 rounded-lg p-4">
          <h3 class="text-sm font-medium text-gray-900 mb-3">
            {{ t('accommodation') }}
          </h3>
          <table class="invoice-table">
            <tbody>
              <tr
                v-for="item in accommodationItems"
                :key="item.product_name"
                class="border-b border-gray-100 last:border-0"
              >
                <td class="py-3">
                  <div class="font-medium text-gray-900">
                    {{ item.product_name }}
                  </div>
                </td>
                <td class="amount text-gray-900">
                  {{ formatCurrency(oreToSEK(item.total)) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Charge items -->
        <div v-if="chargeItems.length" class="mb-4 bg-gray-50 rounded-lg p-4">
          <h3 class="text-sm font-medium text-gray-900 mb-3">
            {{ t('charges') }}
          </h3>
          <table class="invoice-table">
            <tbody>
              <tr
                v-for="item in chargeItems"
                :key="item.id"
                class="border-b border-gray-100 last:border-0"
              >
                <td class="py-3">
                  <div class="font-medium text-gray-900">
                    {{ item.product_name }}
                  </div>
                  <div class="text-sm text-gray-500">
                    {{ formatQuantity(item.quantity) }} ×
                    {{ formatCurrency(oreToSEK(item.price)) }}
                  </div>
                  <div
                    v-if="item.product_description || item.note"
                    class="text-sm text-gray-400 italic"
                  >
                    {{ item.product_description || item.note }}
                  </div>
                </td>
                <td class="amount text-gray-900">
                  {{ formatCurrency(oreToSEK(item.total)) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Totals -->
        <div class="border-t border-gray-200 pt-4">
          <!-- Inga utstående avgifter -->
          <div v-if="!hasAmountToPay" class="space-y-1 mb-3">
            <p class="text-sm text-gray-600">{{ t('noChargesInfo') }}</p>
            <p class="text-xs text-gray-400">{{ t('noChargesUpdate') }}</p>
          </div>
          <!-- Totalt -->
          <div v-if="hasAmountToPay" class="flex justify-between text-lg font-semibold">
            <span class="text-gray-900">{{ t('total') }}</span>
            <span class="text-brand">{{ formatCurrency(oreToSEK(totalAmount)) }}</span>
          </div>
        </div>
      </div>

      <!-- Checkout action card -->
      <div v-if="!hasNegativeTotal" class="card p-6 mb-6">
        <!-- Negativ balans – måste besöka receptionen -->
        <!-- (Dold, hanteras av fristående kort ovan) -->

        <!-- Varning – visas bara när utcheckning är tillgänglig (efter 05:00 på rätt dag) -->
        <div
          v-if="checkoutAvailable && !isBeforeFiveAM"
          class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6"
        >
          <div class="flex items-start gap-3">
            <svg
              class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p class="text-sm text-amber-800">
              {{ t('checkoutWarningBody') }}
            </p>
          </div>
        </div>

        <!-- Payment methods (only when checkout is available) -->
        <div v-if="hasAmountToPay && checkoutAvailable && !isBeforeFiveAM" class="mb-6">
          <div class="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
            <div>
              <p class="text-sm font-medium text-gray-900 mb-3">
                {{ t('paymentMethod') }}
              </p>
              <div
                class="grid gap-3 grid-cols-3 sm:[grid-template-columns:repeat(auto-fit,minmax(0,1fr))]"
              >
                <div
                  v-for="method in displayPaymentLogos"
                  :key="method"
                  class="flex items-center justify-center p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                >
                  <img
                    :src="getPaymentMethodLogo(method)"
                    :alt="method"
                    class="h-8 w-auto"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
            <div class="mb-2 text-right">
              <div class="inline-flex items-center gap-2 text-[11px] sm:text-xs text-gray-500">
                <span>{{ t('securePaymentsBy') }}</span>
                <img
                  src="/images/surfboard.svg"
                  alt="Surfboard Payments"
                  class="h-5 w-auto opacity-90"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Tidig banner – visas på fel dag eller innan 05:00 på utcheckningsdagen -->
        <div
          v-if="!checkoutAvailable || isBeforeFiveAM"
          class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4"
        >
          <div class="flex items-center gap-2">
            <svg
              class="w-5 h-5 text-blue-600 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p class="text-sm font-medium text-blue-700">
              {{ t('timeRestriction') }}
            </p>
          </div>
        </div>

        <!-- Error display -->
        <div
          id="checkout-error"
          v-if="checkoutError"
          class="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 mb-4"
          aria-live="polite"
        >
          <div class="font-semibold mb-1 flex items-center gap-1">
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v4m0 4h.01M4.93 4.93l14.14 14.14M12 2a10 10 0 100 20 10 10 0 000-20z"
              />
            </svg>
            {{ t('checkoutProblem') }}
          </div>
          <div class="text-sm">{{ checkoutError }}</div>
        </div>

        <!-- Checkout button -->
        <button
          @click="performCheckout"
          :disabled="processing || isBeforeFiveAM || !checkoutAvailable || hasNegativeTotal"
          class="btn-checkout w-full flex items-center justify-center gap-2 text-lg py-4 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div v-if="processing" class="relative w-5 h-5" role="status">
            <svg class="animate-spin text-white" viewBox="0 0 50 50">
              <circle
                class="opacity-30 stroke-current"
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke-width="5"
              />
              <circle
                class="stroke-current"
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke-width="5"
                stroke-linecap="round"
                stroke-dasharray="110"
                stroke-dashoffset="80"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="80;0"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </div>
          <span>{{ processing ? t('pleaseWaitAction') : checkoutButtonLabel }}</span>
        </button>

        <p v-if="automaticCheckout" class="text-sm text-gray-600 mt-4 text-center leading-relaxed">
          {{ t('automaticCheckoutNotice') }}
        </p>

        <!-- Disclaimer -->
        <p
          v-if="checkoutAvailable && !isBeforeFiveAM"
          class="text-xs text-gray-500 mt-4 text-center leading-relaxed"
        >
          {{ hasAmountToPay ? t('checkoutDisclaimerPay') : t('checkoutDisclaimerFree') }}
        </p>
      </div>

      <!-- Kontaktinformation på landningssidan -->
      <div v-if="branding.phone || branding.email" class="card p-4 mb-6">
        <p class="text-sm text-gray-600">
          {{ t('contactQuestions') }}
          <a
            v-if="branding.email"
            :href="`mailto:${branding.email}`"
            class="text-brand hover:underline"
            >{{ branding.email }}</a
          >
          <span v-if="branding.phone && branding.email" class="mx-1">·</span>
          <a v-if="branding.phone" :href="phoneHref" class="text-brand hover:underline">{{
            branding.phone
          }}</a>
        </p>
      </div>

      <!-- CampingOnline branding footer -->
      <div class="text-center">
        <div class="text-sm text-gray-500 mb-2">
          {{ t('selfCheckoutBranding') }}
        </div>
        <div class="flex items-center justify-center">
          <img src="/images/campingonline_logo.svg" alt="CampingOnline" class="h-8" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import axios from 'axios';
import { createI18n } from 'vue-i18n';

// Click outside directive
const vClickOutside = {
  beforeMount(el: any, binding: any) {
    el.clickOutsideEvent = (event: Event) => {
      if (!(el === event.target || el.contains(event.target as Node))) {
        binding.value();
      }
    };
    document.addEventListener('click', el.clickOutsideEvent);
  },
  unmounted(el: any) {
    document.removeEventListener('click', el.clickOutsideEvent);
  },
};

const messages = {
  sv: {
    paymentRemaining: 'Betalning återstår',
    nothingToPay: 'Inget återstår att betala.',
    checkedOutPaymentRemaining: 'Du är utcheckad. Betala återstående avgifter via länkarna nedan.',
    invoice: 'Faktura',
    payInvoice: 'Betala faktura',
    invoicePaymentInstructions: 'Se betalningsuppgifterna på fakturan.',

    loading: 'Laddar',
    fetchingBooking: 'Hämtar bokning',
    pleaseWait: 'Vänligen vänta...',
    pleaseWaitAction: 'Vänligen vänta...',
    selfCheckout: 'Självutcheckning',
    consumptionWarning:
      'Elförbrukningen eller eluttaget behöver följas upp av receptionen. Senast tillgängliga avgifter används. Du kan fortsätta med utcheckningen.',
    bookingDetails: 'Bokning',
    bookingNumber: 'Bokningsnr',
    pitch: 'Plats',
    checkIn: 'Incheckning',
    checkOut: 'Utcheckning',
    checkOutDeadline: 'Utcheckning senast',
    checkOutDeadlineHelp: 'Kontakta receptionen för att förlänga din bokning.',
    accommodation: 'Boende',
    charges: 'Avgifter',
    total: 'Totalt',
    checkoutAndPay: 'Checka ut och betala',
    checkoutOnly: 'Checka ut',
    checkoutWarningBody:
      'Checka inte ut förrän ni är redo att lämna campingen – din kod och eventuell tillgång till el kommer att upphöra i samband med utcheckningen.',
    paymentMethod: 'Betalning sker via',
    securePaymentsBy: 'Säkra betalningar tillhandahålls av',
    automaticCheckoutNotice:
      'Om du inte checkar ut själv avslutas bokningen automatiskt på avresedagen. Finns det obetalda avgifter får du en faktura med betalningslänk via mejl.',
    checkoutDisclaimerFree:
      'Det finns inga fler avgifter på din bokning just nu – checka ut när du är redo.',
    checkoutDisclaimerPay:
      'Fortsätt till betalning för att slutföra din utcheckning. Kontakta receptionen innan betalning om du har frågor om specifikationen ovan.',
    checkoutProblem: 'Något gick fel',
    verifyingPayment: 'Verifierar betalning...',
    checkoutConfirmedTitle: 'Utcheckning genomförd!',
    checkoutConfirmedSubtitle: 'Du är nu utcheckad. Tack för din vistelse!',
    paymentReceivedTitle: 'Betalning mottagen',
    paymentReceivedSubtitle: 'En bekräftelse har skickats till din e-postadress',
    bookingSummary: 'Bokningssammanfattning',
    selfCheckoutBranding: 'Självutcheckning är en säker tjänst från',
    fetchErrorTitle: 'Kunde inte hämta bokning',
    errorNotFound: 'Länken är inte längre giltig. Kontakta receptionen.',
    errorTooEarly: 'Självutcheckning är tillgänglig på utcheckningsdagen.',
    errorInvoice: 'Ett fel uppstod vid skapande av faktura. Kontakta receptionen.',
    errorPayment:
      'Utcheckning genomförd men betallänk kunde inte skapas. Du får en faktura via mail.',
    errorGeneric: 'Ett oväntat fel uppstod. Försök igen eller kontakta receptionen.',
    paymentFailed:
      'Betalningsförsöket kunde inte slutföras. Försök igen med en annan betalningsmetod eller kom till receptionen så hjälper vi dig.',
    paymentPending:
      'Vi har ännu inte kunnat bekräfta betalningen. Du kan försöka igen – om båda betalningarna slutförs registreras det extra beloppet på ditt kundsaldo.',
    errorMissingParams: 'Ogiltig länk.',
    errorInvoiceNotFound: 'Fakturan hittades inte. Kontakta receptionen.',
    alreadyCheckedOut: 'Denna bokning är redan utcheckad. Kontakta receptionen vid frågor.',
    timeRestriction: 'Självutcheckning är tillgängligt från kl 05:00 på utcheckningsdagen.',
    contactQuestions: 'Vid frågor, kontakta campingen på',
    feedbackTitle: 'Hur upplevde du vår självutcheckningstjänst?',
    feedbackPlaceholder: 'Dela gärna dina tankar om självutcheckningen...',
    feedbackSubmit: 'Skicka',
    feedbackThanks: 'Tack för din feedback!',
    noChargesInfo: 'Det finns just nu inga utestående avgifter på din bokning.',
    noChargesUpdate: 'Din bokning kommer uppdateras löpande fram till utcheckning.',
    welcomeGreeting: 'Hoppas du har haft en trevlig vistelse, {name}!',
    negativeBalanceTitle: 'Något verkar inte stämma',
    negativeBalanceBody:
      'Vi behöver hjälpa dig med er bokning i receptionen. Kom in till oss så löser vi det!',
    errorNegativeBalance:
      'Bokningen har ett negativt saldo. Vänligen besök receptionen för reglering.',
    landingTitle: 'CampingOnline Självutcheckning',
    landingDescription:
      'Detta är en publik endpoint för CampingOnline Självutcheckning – en del av Sveriges snabbast växande bokningssystem helt inriktat på camping.',
    landingLinkRequired: 'För att checka ut krävs en komplett länk från din utcheckningsinbjudan.',
    landingVisitSite: 'Besök campingonline.se',
  },
  en: {
    paymentRemaining: 'Payment remaining',
    nothingToPay: 'Nothing remains to pay.',
    checkedOutPaymentRemaining:
      'You are checked out. Pay your remaining charges using the links below.',
    invoice: 'Invoice',
    payInvoice: 'Pay invoice',
    invoicePaymentInstructions: 'See the payment instructions on your invoice.',

    loading: 'Loading',
    fetchingBooking: 'Loading booking',
    pleaseWait: 'Please wait...',
    pleaseWaitAction: 'Please wait...',
    selfCheckout: 'Self-Checkout',
    consumptionWarning:
      'Reception needs to follow up on the electricity reading or outlet. The latest available charges are used. You can continue checking out.',
    bookingDetails: 'Booking',
    bookingNumber: 'Booking no.',
    pitch: 'Pitch',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    checkOutDeadline: 'Latest checkout',
    checkOutDeadlineHelp: 'Contact reception to extend your booking.',
    accommodation: 'Accommodation',
    charges: 'Charges',
    total: 'Total',
    checkoutAndPay: 'Check out and pay',
    checkoutOnly: 'Check out',
    checkoutWarningBody:
      'Do not check out until you are ready to leave the campsite – your access code and any electricity supply will be discontinued at checkout.',
    paymentMethod: 'Payment via',
    securePaymentsBy: 'Secure payments provided by',
    automaticCheckoutNotice:
      'If you do not check out yourself, your booking will be checked out automatically on your departure day. If any charges remain unpaid, you will receive an invoice with a payment link by email.',
    checkoutDisclaimerFree:
      'There are no more charges on this booking right now – check out when ready.',
    checkoutDisclaimerPay:
      'Proceed to payment to finalise your checkout. Contact reception before payment if you have any questions about the specification above.',
    checkoutProblem: 'Something went wrong',
    verifyingPayment: 'Verifying payment...',
    checkoutConfirmedTitle: 'Checkout complete!',
    checkoutConfirmedSubtitle: 'You are now checked out. Thank you for your stay!',
    paymentReceivedTitle: 'Payment received',
    paymentReceivedSubtitle: 'A confirmation has been sent to your email address',
    bookingSummary: 'Booking summary',
    selfCheckoutBranding: 'Self-checkout is a secure service by',
    fetchErrorTitle: 'Could not load booking',
    errorNotFound: 'This link is no longer valid. Please contact the reception.',
    errorTooEarly: 'Self-checkout is available on the checkout day.',
    errorInvoice: 'An error occurred while creating the invoice. Please contact the reception.',
    errorPayment:
      'Checkout completed but payment link could not be created. You will receive an invoice by email.',
    errorGeneric: 'An unexpected error occurred. Please try again or contact the reception.',
    paymentFailed: 'The payment attempt could not be completed. Please try again.',
    paymentPending:
      'We have not yet been able to confirm the payment. You can try again – if both payments complete, the extra amount will be registered on your customer balance.',
    errorMissingParams: 'Invalid link.',
    errorInvoiceNotFound: 'Invoice not found. Please contact the reception.',
    alreadyCheckedOut: 'This booking has already been checked out. Please contact the reception.',
    timeRestriction: 'Self-checkout is available from 05:00 on the checkout day.',
    contactQuestions: 'For questions, contact the campsite at',
    feedbackTitle: 'How was your self-checkout experience?',
    feedbackPlaceholder: 'Share your thoughts on the self-checkout process...',
    feedbackSubmit: 'Submit',
    feedbackThanks: 'Thank you for your feedback!',
    noChargesInfo: 'There are currently no outstanding charges on your booking.',
    noChargesUpdate: 'Your booking will be updated continuously until checkout.',
    welcomeGreeting: "We hope you've had a great stay, {name}!",
    negativeBalanceTitle: "Something doesn't look right",
    negativeBalanceBody:
      "We need to help you with your booking at the reception. Please come in and we'll sort it out!",
    errorNegativeBalance:
      'The booking has a negative balance. Please visit the reception to settle it.',
    landingTitle: 'CampingOnline Self-Checkout',
    landingDescription:
      "This is a public endpoint for CampingOnline Self-Checkout – part of Sweden's fastest growing booking system dedicated to camping.",
    landingLinkRequired: 'A complete link from your checkout invitation is required to check out.',
    landingVisitSite: 'Visit campingonline.se',
  },
  de: {
    paymentRemaining: 'Zahlung ausstehend',
    nothingToPay: 'Es ist nichts mehr zu bezahlen.',
    checkedOutPaymentRemaining:
      'Sie sind ausgecheckt. Begleichen Sie offene Beträge über die folgenden Links.',
    invoice: 'Rechnung',
    payInvoice: 'Rechnung bezahlen',
    invoicePaymentInstructions: 'Die Zahlungsinformationen finden Sie auf Ihrer Rechnung.',

    loading: 'Laden',
    fetchingBooking: 'Buchung wird geladen',
    pleaseWait: 'Bitte warten...',
    pleaseWaitAction: 'Bitte warten...',
    selfCheckout: 'Selbst-Checkout',
    consumptionWarning:
      'Die Rezeption muss den Stromzählerstand oder den Stromanschluss überprüfen. Es werden die zuletzt verfügbaren Gebühren verwendet. Sie können mit dem Check-out fortfahren.',
    bookingDetails: 'Buchung',
    bookingNumber: 'Buchungsnr.',
    pitch: 'Stellplatz',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    checkOutDeadline: 'Spätestes Auschecken',
    checkOutDeadlineHelp: 'Kontaktieren Sie die Rezeption, um Ihren Aufenthalt zu verlängern.',
    accommodation: 'Unterkunft',
    charges: 'Gebühren',
    total: 'Gesamt',
    checkoutAndPay: 'Auschecken und bezahlen',
    checkoutOnly: 'Auschecken',
    checkoutWarningBody:
      'Checken Sie nicht aus, bis Sie bereit sind den Campingplatz zu verlassen – Ihr Zugangscode und die Stromversorgung werden beim Auschecken deaktiviert.',
    paymentMethod: 'Zahlung über',
    securePaymentsBy: 'Sichere Zahlungen bereitgestellt von',
    automaticCheckoutNotice:
      'Wenn Sie nicht selbst auschecken, wird Ihre Buchung am Abreisetag automatisch beendet. Wenn noch Beträge offen sind, erhalten Sie per E-Mail eine Rechnung mit Zahlungslink.',
    checkoutDisclaimerFree:
      'Es gibt keine zusätzlichen Gebühren für diese Buchung – Sie können auschecken, wenn Sie bereit sind.',
    checkoutDisclaimerPay:
      'Fahren Sie zur Zahlung fort, um Ihren Checkout abzuschließen. Kontaktieren Sie die Rezeption vor der Zahlung, wenn Sie Fragen zur obigen Spezifikation haben.',
    checkoutProblem: 'Etwas ist schiefgelaufen',
    verifyingPayment: 'Zahlung wird überprüft...',
    checkoutConfirmedTitle: 'Checkout abgeschlossen!',
    checkoutConfirmedSubtitle: 'Sie sind jetzt ausgecheckt. Vielen Dank für Ihren Aufenthalt!',
    paymentReceivedTitle: 'Zahlung erhalten',
    paymentReceivedSubtitle: 'Eine Bestätigung wurde an Ihre E-Mail-Adresse gesendet',
    bookingSummary: 'Buchungsübersicht',
    selfCheckoutBranding: 'Self-Checkout ist ein sicherer Service von',
    fetchErrorTitle: 'Buchung konnte nicht geladen werden',
    errorNotFound: 'Dieser Link ist nicht mehr gültig. Bitte kontaktieren Sie die Rezeption.',
    errorTooEarly: 'Self-Checkout ist am Abreisetag verfügbar.',
    errorInvoice:
      'Beim Erstellen der Rechnung ist ein Fehler aufgetreten. Bitte kontaktieren Sie die Rezeption.',
    errorPayment:
      'Checkout abgeschlossen, aber Zahlungslink konnte nicht erstellt werden. Sie erhalten eine Rechnung per E-Mail.',
    errorGeneric:
      'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie die Rezeption.',
    paymentFailed:
      'Der Zahlungsversuch konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.',
    paymentPending:
      'Wir konnten die Zahlung noch nicht bestätigen. Sie können es erneut versuchen – wenn beide Zahlungen abgeschlossen werden, wird der zusätzliche Betrag Ihrem Kundenguthaben gutgeschrieben.',
    errorMissingParams: 'Ungültiger Link.',
    errorInvoiceNotFound: 'Rechnung nicht gefunden. Bitte kontaktieren Sie die Rezeption.',
    alreadyCheckedOut:
      'Diese Buchung wurde bereits ausgecheckt. Bitte kontaktieren Sie die Rezeption.',
    timeRestriction: 'Self-Checkout ist ab 05:00 am Abreisetag verfügbar.',
    contactQuestions: 'Bei Fragen kontaktieren Sie den Campingplatz unter',
    feedbackTitle: 'Wie war Ihr Self-Checkout-Erlebnis?',
    feedbackPlaceholder: 'Teilen Sie Ihre Gedanken zum Self-Checkout-Prozess...',
    feedbackSubmit: 'Absenden',
    feedbackThanks: 'Vielen Dank für Ihr Feedback!',
    noChargesInfo: 'Für Ihre Buchung gibt es derzeit keine ausständigen Gebühren.',
    noChargesUpdate: 'Ihre Buchung wird kontinuierlich bis zum Auschecken aktualisiert.',
    welcomeGreeting: 'Wir hoffen, Sie hatten einen schönen Aufenthalt, {name}!',
    negativeBalanceTitle: 'Etwas scheint nicht zu stimmen',
    negativeBalanceBody:
      'Wir müssen Ihnen an der Rezeption mit Ihrer Buchung helfen. Kommen Sie gerne vorbei!',
    errorNegativeBalance:
      'Die Buchung hat ein negatives Saldo. Bitte besuchen Sie die Rezeption zur Klärung.',
    landingTitle: 'CampingOnline Selbst-Checkout',
    landingDescription:
      'Dies ist ein öffentlicher Endpunkt für CampingOnline Selbst-Checkout – Teil von Schwedens am schnellsten wachsendem Buchungssystem, spezialisiert auf Camping.',
    landingLinkRequired:
      'Zum Auschecken benötigen Sie einen vollständigen Link aus Ihrer Checkout-Einladung.',
    landingVisitSite: 'Besuchen Sie campingonline.se',
  },
};

const urlLang = new URLSearchParams(window.location.search).get('lang');
const initialLocale = urlLang === 'en' || urlLang === 'de' ? urlLang : 'sv';
const i18n = createI18n({
  legacy: false,
  locale: initialLocale,
  fallbackLocale: 'sv',
  messages,
});
const { t, locale } = i18n.global;

// Locale switcher
const availableLocales = ref([
  { code: 'sv', name: 'Svenska', flag: '/images/flag-sv.svg' },
  { code: 'en', name: 'English', flag: '/images/flag-en.svg' },
  { code: 'de', name: 'Deutsch', flag: '/images/flag-de.svg' },
]);
const currentLocale = computed(() => locale.value);
const currentLocaleData = computed(
  () =>
    availableLocales.value.find(l => l.code === currentLocale.value) || availableLocales.value[0]
);
const isDropdownOpen = ref(false);
const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};
const closeDropdown = () => {
  isDropdownOpen.value = false;
};
const switchLocale = (newLocale: string) => {
  if (newLocale === 'sv' || newLocale === 'en' || newLocale === 'de') {
    locale.value = newLocale as 'sv' | 'en' | 'de';
    const url = new URL(window.location.href);
    url.searchParams.set('lang', newLocale);
    window.history.replaceState({}, '', url.toString());
    closeDropdown();
  }
};

// --- Routing ---
// Production path: /{namespace}/{session_key}
// Local dev path:  /{namespace}/checkout/{session_key}
const segments = window.location.pathname.replace(/^\/+/, '').split('/');
const routeNamespace = segments[0] || '';
const hasCheckoutSegment = segments[1] === 'checkout';
const routeSessionKey = hasCheckoutSegment ? segments[2] || '' : segments[1] || '';

const queryParams = new URLSearchParams(window.location.search);
const invoiceIdParam = queryParams.get('invoice_id');
const invoiceCodeParam = queryParams.get('invoice_code');
const paymentAttemptIdParam = queryParams.get('payment_attempt_id');

// --- State ---
const loading = ref(true);
const loadError = ref<string | null>(null);
const loadErrorCode = ref<string | null>(null);
const processing = ref(false);
const checkoutError = ref<string | null>(null);
const hasConsumptionWarning = ref(false);
const checkoutCompleted = ref(false);
const paymentConfirmed = ref(false);
const waitingForPayment = ref(false);
const genericLanding = ref(false);

// Branding
const branding = reactive({
  name: '',
  logo: '',
  phone: '',
  email: '',
});
const phoneHref = computed(() => 'tel:' + branding.phone.replace(/[^+0-9]/g, ''));

// Booking preview data
const booking = reactive({
  id: 0,
  bookingNumber: '',
  checkIn: '',
  checkOut: '',
  positionName: '',
  positionLabel: '',
  customerName: '',
  checkOutTime: '',
});
const accommodationItems = ref<
  Array<{
    product_name: string;
    quantity: number;
    price: number;
    total: number;
  }>
>([]);
const chargeItems = ref<
  Array<{
    id: number;
    product_name: string;
    product_description: string | null;
    quantity: number;
    price: number;
    total: number;
    note: string | null;
  }>
>([]);
const totalAmount = ref(0);
const hasAmountToPay = ref(false);
const hasNegativeTotal = ref(false);
const remainingAmount = ref<number | null>(null);
const outstandingInvoices = ref<
  Array<{
    id: number;
    invoice_number: string;
    remaining_amount: number;
    payment_link: string | null;
  }>
>([]);

// 05:00 tidssäkerhetspärr
const isBeforeFiveAM = computed(() => {
  return new Date().getHours() < 5;
});

// Feedback
const feedbackRating = ref(0);
const feedbackComment = ref('');
const feedbackSubmitted = ref(false);
const feedbackSubmitting = ref(false);

// E-post för bekräftelsemeddelandet
const confirmationEmail = ref('');

// Utcheckning inte tillgänglig ännu (för tidig)
const checkoutAvailable = ref(true);
const automaticCheckout = ref(false);

// Stjärnhovring för feedbackformulär
const feedbackHoverRating = ref(0);

// Helpers
const oreToSEK = (v: number) => v / 100;

const getApiBase = () => {
  const host = window.location.hostname;
  const isLocal = /localhost|127\.|\.test$/i.test(host) || routeNamespace === 'local';
  if (isLocal) return 'https://server.test';
  if (routeNamespace) return `https://${routeNamespace}.campingonline.se`;
  return 'https://server.test';
};

// Gemensamma headers med språkinställning
const apiHeaders = computed(() => ({
  Accept: 'application/json',
  'Accept-Language': currentLocale.value,
}));

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('sv-SE');
};

const formatTime = (timeStr: string) => {
  if (!timeStr) return '';
  return timeStr.substring(0, 5);
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

const formatQuantity = (qty: number) => {
  const val = qty / 1000;
  if (Number.isInteger(val)) return `${val} st`;
  return `${val.toFixed(1)}`;
};

// Error message mapping
const getErrorMessage = (errorCode: string, fallbackMessage: string): string => {
  const map: Record<string, string> = {
    not_found: t('errorNotFound'),
    already_checked_out: t('alreadyCheckedOut'),
    too_early: t('errorTooEarly'),
    invoice_error: t('errorInvoice'),
    payment_error: t('errorPayment'),
    missing_params: t('errorMissingParams'),
    invoice_not_found: t('errorInvoiceNotFound'),
    negative_balance: t('errorNegativeBalance'),
  };
  return map[errorCode] || fallbackMessage || t('errorGeneric');
};

// Reaktivt felmeddelande – uppdateras när språket ändras
const displayLoadError = computed(() => {
  if (!loadError.value && !loadErrorCode.value) return null;
  if (loadErrorCode.value) return getErrorMessage(loadErrorCode.value, loadError.value || '');
  return loadError.value;
});

// Förnamn ur fullständigt namn för personlig hälsning
const customerFirstName = computed(() => {
  if (!booking.customerName) return '';
  return booking.customerName.split(' ')[0];
});

// Error styling based on error code
const errorStyling = computed(() => {
  if (loadErrorCode.value === 'too_early') {
    return {
      containerClass: 'border-amber-200 bg-amber-50 text-amber-700',
      title: t('fetchErrorTitle'),
    };
  }
  if (loadErrorCode.value === 'already_checked_out') {
    return {
      containerClass: 'border-blue-200 bg-blue-50 text-blue-700',
      title: t('fetchErrorTitle'),
    };
  }
  return {
    containerClass: 'border-red-200 bg-red-50 text-red-700',
    title: t('fetchErrorTitle'),
  };
});

// Betalningsmetoder från servern
const paymentMethods = ref<string[]>([]);
const displayPaymentLogos = computed(() => {
  const methodMap: Record<string, string[]> = {
    CARD: ['visa', 'mastercard', 'applepay', 'googlepay'],
    SWISH: ['swish'],
    APPLEPAY: ['applepay'],
    GOOGLEPAY: ['googlepay'],
    KLARNA: ['klarna'],
    AMEX: ['amex'],
  };
  if (!paymentMethods.value.length) return ['visa', 'mastercard', 'swish', 'applepay', 'googlepay'];
  return paymentMethods.value.flatMap(m => methodMap[m] || []);
});
const getPaymentMethodLogo = (method: string) => {
  const logos: Record<string, string> = {
    visa: '/images/visa.svg',
    mastercard: '/images/mastercard.svg',
    swish: '/images/swish.svg',
    applepay: '/images/applepay.svg',
    googlepay: '/images/googlepay.svg',
    klarna: '/images/klarna.png',
    amex: '/images/amex.svg',
  };
  return logos[method] || '';
};

// Checkout button label
const checkoutButtonLabel = computed(() =>
  hasAmountToPay.value ? t('checkoutAndPay') : t('checkoutOnly')
);

// --- Fetch branding ---
const fetchBranding = async () => {
  try {
    const resp = await axios.get(`${getApiBase()}/branding`, {
      headers: apiHeaders.value,
    });
    if (resp.data?.status === 'success' || resp.data?.data) {
      const data = resp.data?.data ?? resp.data;
      branding.name = data.name || '';
      branding.logo = data.logo?.url || '';
      branding.phone = data.phone || '';
      branding.email = data.email || '';
    }
  } catch {
    // Branding is non-critical
  }
};

// --- Fetch preview ---
const fetchPreview = async () => {
  if (!routeNamespace) {
    genericLanding.value = true;
    loading.value = false;
    return;
  }
  if (!routeSessionKey) {
    loadErrorCode.value = 'not_found';
    loading.value = false;
    return;
  }

  try {
    loading.value = true;
    loadError.value = null;
    loadErrorCode.value = null;

    await fetchBranding();

    const url = `${getApiBase()}/self-checkout/${routeSessionKey}/preview`;
    const resp = await axios.get(url, {
      headers: apiHeaders.value,
    });

    if (resp.data.status === 'error') {
      loadErrorCode.value = resp.data.error_code || null;
      return;
    }

    const data = resp.data.data;
    if (!data) throw new Error(t('errorGeneric'));

    hasConsumptionWarning.value = Boolean(data.electricity_status);
    booking.id = data.booking.id;
    booking.bookingNumber = data.booking.booking_number;
    booking.checkIn = data.booking.check_in;
    booking.checkOut = data.booking.check_out;
    booking.positionName = data.booking.position_name;
    booking.positionLabel = data.booking.position_label || '';
    booking.customerName = data.booking.customer_name;
    booking.checkOutTime = data.booking.check_out_time;

    accommodationItems.value = data.accommodation_items || [];
    chargeItems.value = data.charge_items || [];
    totalAmount.value = data.total_amount || 0;
    hasAmountToPay.value = data.has_amount_to_pay ?? data.total_amount > 0;
    hasNegativeTotal.value = data.has_negative_total === true;
    paymentMethods.value = data.surfboard_payment_methods || [];
    checkoutAvailable.value = data.checkout_available !== false;
    automaticCheckout.value = data.automatic_checkout === true;
  } catch (e) {
    loadError.value = (e instanceof Error ? e.message : null) || t('errorGeneric');
  } finally {
    loading.value = false;
  }
};

// --- Hämta bekräftelsedata för redan utcheckad bokning ---
const fetchConfirmation = async () => {
  try {
    await fetchBranding();
    const resp = await axios.get(`${getApiBase()}/self-checkout/${routeSessionKey}/confirmation`, {
      headers: apiHeaders.value,
    });
    if (resp.data?.data) {
      const data = resp.data.data;
      hasConsumptionWarning.value = Boolean(data.electricity_status);
      if (data.booking) {
        booking.bookingNumber = data.booking.booking_number || '';
        booking.checkIn = data.booking.check_in || '';
        booking.checkOut = data.booking.check_out || '';
        booking.positionName = data.booking.position_name || '';
        booking.positionLabel = data.booking.position_label || '';
        booking.customerName = data.booking.customer_name || '';
        confirmationEmail.value = data.booking.customer_email || '';
      }
      remainingAmount.value = data.remaining_amount ?? null;
      outstandingInvoices.value = data.outstanding_invoices ?? [];
      paymentConfirmed.value = Boolean(data.had_payment) && (remainingAmount.value ?? 0) === 0;
      checkoutCompleted.value = true;
      loadError.value = null;
      loadErrorCode.value = null;
    }
  } catch {
    // Behåll befintligt felmeddelande om bekräftelse inte kan hämtas
  }
};

// --- Perform checkout ---
const performCheckout = async () => {
  if (processing.value) return;
  checkoutError.value = null;
  processing.value = true;

  try {
    // Bygg redirect-URL:er med platshållare som servern ersätter efter fakturaskapande
    const selfPath = hasCheckoutSegment
      ? `/${routeNamespace}/checkout/${routeSessionKey}`
      : `/${routeNamespace}/${routeSessionKey}`;
    const origin = window.location.origin;
    const successUrl = `${origin}${selfPath}?status=success&invoice_id={invoice_id}&invoice_code={invoice_code}`;
    const errorUrl = `${origin}${selfPath}?payment_failed=1&invoice_id={invoice_id}&invoice_code={invoice_code}`;

    const url = `${getApiBase()}/self-checkout/${routeSessionKey}/checkout`;
    const resp = await axios.post(
      url,
      {
        success_url: successUrl,
        error_url: errorUrl,
      },
      {
        headers: {
          ...apiHeaders.value,
          'Content-Type': 'application/json',
        },
      }
    );

    if (resp.data.status === 'error') {
      if (resp.data.error_code === 'already_checked_out') {
        await fetchConfirmation();
        if (checkoutCompleted.value) return;
      }
      throw new Error(getErrorMessage(resp.data.error_code, resp.data.error_message));
    }

    const data = resp.data.data;
    if (!data) throw new Error(t('errorGeneric'));

    hasConsumptionWarning.value = Boolean(data.electricity_status);
    if (data.status === 'completed' && !data.has_payment) {
      checkoutCompleted.value = true;
      await fetchConfirmation();
      return;
    }

    if (data.status === 'payment_required' && data.payment_link) {
      window.location.href = data.payment_link;
      return;
    }

    throw new Error(t('errorGeneric'));
  } catch (e) {
    checkoutError.value = (e instanceof Error ? e.message : null) || t('errorGeneric');
  } finally {
    processing.value = false;
  }
};

// --- Check payment status (after redirect) ---
const checkPaymentStatus = async () => {
  if (!invoiceIdParam || !invoiceCodeParam) return;

  waitingForPayment.value = true;
  await fetchBranding();

  let polls = 0;
  const maxPolls = 12;

  const showUnconfirmedPayment = async (message: string) => {
    waitingForPayment.value = false;
    await fetchPreview();
    if (loadErrorCode.value === 'already_checked_out') {
      await fetchConfirmation();
      if (checkoutCompleted.value) return;
    }
    checkoutError.value = message;

    const url = new URL(window.location.href);
    ['status', 'payment_failed', 'invoice_id', 'invoice_code', 'payment_attempt_id'].forEach(
      param => url.searchParams.delete(param)
    );
    window.history.replaceState({}, '', url.toString());
  };

  const poll = async (): Promise<void> => {
    try {
      const url = `${getApiBase()}/self-checkout/payment-status`;
      const resp = await axios.get(url, {
        params: {
          invoice_id: invoiceIdParam,
          invoice_code: invoiceCodeParam,
          payment_attempt_id: paymentAttemptIdParam,
        },
        headers: apiHeaders.value,
      });

      if (resp.data?.data?.is_paid) {
        hasConsumptionWarning.value = Boolean(resp.data.data.electricity_status);
        // Fyll i bokningsdata från svar
        if (resp.data.data.booking) {
          const b = resp.data.data.booking;
          booking.bookingNumber = b.booking_number || '';
          booking.checkIn = b.check_in || '';
          booking.checkOut = b.check_out || '';
          booking.positionName = b.position_name || '';
          booking.customerName = b.customer_name || '';
          confirmationEmail.value = b.customer_email || '';
        }
        paymentConfirmed.value = true;
        checkoutCompleted.value = true;
        waitingForPayment.value = false;
        await fetchConfirmation();
        return;
      }

      if (resp.data?.data?.payment_attempt_status === 'cancelled') {
        await showUnconfirmedPayment(t('paymentFailed'));
        return;
      }

      polls++;
      if (polls < maxPolls) {
        await new Promise(resolve => setTimeout(resolve, 5000));
        return poll();
      }

      await showUnconfirmedPayment(t('paymentPending'));
    } catch {
      await showUnconfirmedPayment(t('paymentPending'));
    }
  };

  await poll();
};

// --- Feedback ---
const submitFeedback = async () => {
  if (feedbackSubmitting.value || feedbackRating.value === 0) return;
  feedbackSubmitting.value = true;
  try {
    await axios.post(
      `${getApiBase()}/web/user-feedback`,
      {
        booking_id: booking.bookingNumber,
        rating: feedbackRating.value,
        comments: feedbackComment.value || '',
        source: 'checkout',
      },
      {
        headers: {
          ...apiHeaders.value,
          'Content-Type': 'application/json',
        },
      }
    );
    feedbackSubmitted.value = true;
  } catch {
    // Feedback är inte kritiskt
  } finally {
    feedbackSubmitting.value = false;
  }
};

// --- Mount ---
onMounted(async () => {
  const params = new URLSearchParams(window.location.search);

  // Redirect tillbaka från betalningssidan vid lyckad betalning
  if (
    (params.get('status') === 'success' || params.get('payment_failed') === '1') &&
    invoiceIdParam &&
    invoiceCodeParam
  ) {
    loading.value = false;
    await checkPaymentStatus();
    return;
  }

  // Misslyckad betalning – visa varning och ladda preview som vanligt
  const paymentFailed = params.get('payment_failed') === '1';
  if (paymentFailed) {
    checkoutError.value = t('paymentFailed');
    const url = new URL(window.location.href);
    url.searchParams.delete('payment_failed');
    window.history.replaceState({}, '', url.toString());
  }

  await fetchPreview();

  // Scrolla till felmeddelandet om betalningen misslyckades
  if (paymentFailed) {
    await new Promise(resolve => setTimeout(resolve, 50));
    document
      .getElementById('checkout-error')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // Om bokningen redan är utcheckad och session_key finns kvar – visa bekräftelseskärmen
  if (loadErrorCode.value === 'already_checked_out' && routeSessionKey) {
    await fetchConfirmation();
  }
});
</script>
